import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "@/components/MaskedLine";
import { Send, Phone, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const services = ["Élagage / Abattage", "Taille de haies", "Jardinage paysager", "Arboriculture / Diagnostic", "Entretien régulier", "Autre demande"];

export const QuoteSection = () => {
  const [form, setForm] = useState({ nom: "", telephone: "", email: "", service: services[0], message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim() || !form.telephone.trim()) {
      toast.error("Merci d'indiquer au moins votre nom et votre téléphone.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/devis`, form);
      setSent(true);
      toast.success("Demande envoyée ! Nous vous rappelons sous 24h.");
    } catch {
      toast.error("Une erreur est survenue. Réessayez ou appelez-nous directement.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-[#F5F3EE] border border-[#2D5A3F]/15 rounded-xl px-5 py-4 text-[#17231A] placeholder-[#47594D]/50 focus:outline-none focus:ring-2 focus:ring-[#2D5A3F]/40 focus:border-[#2D5A3F] transition-shadow duration-300";

  return (
    <section id="contact" data-testid="quote-section" className="py-20 sm:py-28 lg:py-36 bg-[#F5F3EE]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="font-mono-rb text-xs uppercase tracking-[0.25em] text-[#9E5A3C] mb-6">Devis gratuit</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif-rb text-3xl sm:text-4xl lg:text-5xl text-[#17231A] leading-tight" data-testid="quote-title">
                Parlons de <span className="italic text-[#2D5A3F]">votre jardin.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-[#47594D] leading-relaxed">
                Décrivez votre projet en quelques lignes : nous vous rappelons sous 24h ouvrées
                avec un premier avis et un rendez-vous pour une visite technique gratuite.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-10 space-y-4">
                <a
                  href="tel:+33603698346"
                  data-testid="quote-phone-link"
                  className="flex items-center gap-4 bg-[#121F17] text-[#F5F3EE] rounded-2xl px-6 py-5 group hover:bg-[#1C2B22] transition-colors duration-300"
                >
                  <span className="w-11 h-11 rounded-full bg-[#2D5A3F] flex items-center justify-center group-hover:bg-[#D4AF37] group-hover:text-[#121F17] transition-colors duration-300">
                    <Phone className="w-5 h-5" />
                  </span>
                  <span>
                    <span className="block text-xs text-[#8CA893] uppercase tracking-widest font-mono-rb">Urgences &amp; renseignements</span>
                    <span className="font-semibold">06 03 69 83 46</span>
                  </span>
                </a>
                <div className="flex items-center gap-3 text-sm text-[#47594D] px-2">
                  <CheckCircle2 className="w-4 h-4 text-[#2D5A3F]" /> Visite technique et devis sans engagement
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15} className="lg:col-span-7">
            {sent ? (
              <div data-testid="quote-success" className="h-full min-h-[420px] flex flex-col items-center justify-center text-center bg-[#121F17] rounded-3xl p-12 rb-grain relative overflow-hidden">
                <CheckCircle2 className="w-14 h-14 text-[#D4AF37] mb-6" />
                <h3 className="font-serif-rb text-3xl text-[#F5F3EE]">Merci, {form.nom.split(" ")[0]} !</h3>
                <p className="mt-4 text-[#F5F3EE]/70 max-w-sm">
                  Votre demande de devis est bien reçue. Nous vous recontactons sous 24h ouvrées.
                </p>
                <button
                  onClick={() => { setSent(false); setForm({ nom: "", telephone: "", email: "", service: services[0], message: "" }); }}
                  data-testid="quote-new-request-btn"
                  className="mt-8 text-sm text-[#D4AF37] border-b border-[#D4AF37]/40 pb-1 hover:border-[#D4AF37] transition-colors"
                >
                  Faire une autre demande
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="quote-form"
                className="bg-white rounded-3xl p-8 sm:p-10 border border-[#2D5A3F]/10 shadow-[0_30px_80px_-40px_rgba(18,31,23,0.3)] space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <input data-testid="quote-input-nom" required value={form.nom} onChange={set("nom")} placeholder="Votre nom *" className={inputCls} />
                  <input data-testid="quote-input-telephone" required value={form.telephone} onChange={set("telephone")} placeholder="Téléphone *" type="tel" className={inputCls} />
                </div>
                <input data-testid="quote-input-email" value={form.email} onChange={set("email")} placeholder="Email (facultatif)" type="email" className={inputCls} />
                <div>
                  <p className="text-xs font-mono-rb uppercase tracking-widest text-[#47594D] mb-3">Type d'intervention</p>
                  <div className="flex flex-wrap gap-2" data-testid="quote-service-pills">
                    {services.map((s) => (
                      <button
                        type="button"
                        key={s}
                        onClick={() => setForm({ ...form, service: s })}
                        data-testid={`quote-service-${s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                        className={`text-xs sm:text-sm rounded-full px-4 py-2.5 border transition-colors duration-300 ${
                          form.service === s
                            ? "bg-[#2D5A3F] text-[#F5F3EE] border-[#2D5A3F]"
                            : "bg-transparent text-[#47594D] border-[#2D5A3F]/20 hover:border-[#2D5A3F]/60"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea
                  data-testid="quote-input-message"
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Décrivez votre projet : type d'arbres, hauteur approximative, surface du jardin, accès…"
                  rows={4}
                  className={`${inputCls} resize-none`}
                />
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="quote-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-3 bg-[#121F17] text-[#F5F3EE] font-semibold rounded-xl px-8 py-4 hover:bg-[#2D5A3F] transition-colors duration-300 disabled:opacity-60"
                >
                  {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-4 h-4" />}
                  {sending ? "Envoi en cours…" : "Envoyer ma demande de devis"}
                </button>
                <p className="text-xs text-[#47594D]/70 text-center">Réponse sous 24h ouvrées — vos données ne sont jamais partagées.</p>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};
